import React, { useState } from 'react';
import { Calendar, Clock, RefreshCw, FileText, Info, Lock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Skeleton } from '../../../components/Skeleton';
import { useHorariosOficiais } from '../../../hooks/useHorariosOficiais';
import { usePermissions, PERMISSOES } from '../../../hooks/usePermissions';
import { DiaSemana } from '../../../types/horariosOficiais';
import { PlanilhaHorarios } from './PlanilhaHorarios';
import { TabRelatorios } from './TabRelatorios';
import { NotificationsConfig } from './NotificationsConfig';
import { useAuth } from '../../../contexts/AuthContext';

const DIAS_SEMANA: { key: DiaSemana; label: string }[] = [
  { key: 'segunda', label: 'Segunda' },
  { key: 'terca', label: 'Terça' },
  { key: 'quarta', label: 'Quarta' },
  { key: 'quinta', label: 'Quinta' },
  { key: 'sexta', label: 'Sexta' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

function formatarData(dataISO: string): string {
  const [ano, mes, dia] = dataISO.split('-');
  return `${dia}/${mes}/${ano}`;
}

export function HorariosOficiais() {
  const { hasPermission } = usePermissions();
  const { militar } = useAuth();
  const {
    dados,
    loading,
    saving,
    carregarHorarios,
    salvar,
    isDiaBloqueado,
    getConfig,
  } = useHorariosOficiais();

  const [abaAtiva, setAbaAtiva] = useState('planilha');

  const podeVisualizar = hasPermission(PERMISSOES.VISUALIZAR_HORARIOS_OFICIAIS);
  const podeEditar = hasPermission(PERMISSOES.EDITAR_HORARIOS_PROPRIOS);
  const podeVisualizarRelatorios = hasPermission(PERMISSOES.VISUALIZAR_RELATORIOS_HORARIOS_OFICIAIS);

  if (!podeVisualizar) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Acesso Negado</h2>
            <p className="text-muted-foreground">
              Você não tem permissão para visualizar os horários de oficiais.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-[1600px]">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <Clock className="w-8 h-8" />
          Horários de Oficiais
        </h1>
        <p className="text-muted-foreground">
          Marque os horários em que você estará online durante a semana
        </p>
      </div>

      {/* Informações da semana */}
      {dados && (
        <Card className="mb-6 border-l-4 border-l-primary">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Semana Atual</p>
                  <p className="text-base font-semibold">
                    {formatarData(dados.semana.inicio)} a {formatarData(dados.semana.fim)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Horário Limite</p>
                  <p className="text-base font-semibold">{dados.horarioLimite}</p>
                  <p className="text-xs text-muted-foreground">Para marcar horários do dia</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Dias Bloqueados</p>
                  <p className="text-base font-semibold">
                    {dados.diasBloqueados.length > 0
                      ? `${dados.diasBloqueados.length} dia(s)`
                      : 'Nenhum'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuração de Notificações */}
      {podeEditar && (
        <div className="mb-6">
          <NotificationsConfig />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={abaAtiva} onValueChange={setAbaAtiva}>
        <TabsList className="mb-4">
          <TabsTrigger value="planilha" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Planilha
          </TabsTrigger>
          <TabsTrigger value="relatorios" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Relatórios
          </TabsTrigger>
        </TabsList>

        <TabsContent value="planilha">
          {loading ? (
            <Card>
              <CardContent className="py-8">
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              </CardContent>
            </Card>
          ) : dados ? (
            <div className="space-y-4">
              {/* Instruções */}
              <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-900">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">
                        Como usar a planilha:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-blue-800 dark:text-blue-200">
                        <li>Selecione um dia da semana no seletor acima da planilha</li>
                        <li>Visualize todos os oficiais e seus horários marcados para aquele dia</li>
                        <li>Clique em "Editar Meus Horários" para marcar/desmarcar seus horários (09h às 23h)</li>
                        <li>Clique nos horários para marcar/desmarcar, ou use "Marcar Ausente" se não puder estar online</li>
                        <li>A última linha mostra quantos oficiais marcaram cada horário (totalizador)</li>
                        <li>
                          Após as {dados.horarioLimite}, o dia atual será bloqueado para edição
                        </li>
                        <li>As marcações são resetadas toda segunda-feira às 04:00</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Planilha */}
              <PlanilhaHorarios
                dados={dados}
                podeEditar={podeEditar}
                idMilitarLogado={militar?.id || ''}
                onSalvar={salvar}
                salvando={saving}
              />

              {/* Botão de atualizar */}
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={carregarHorarios}
                  disabled={loading}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar Planilha
                </Button>
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Nenhum dado disponível</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="relatorios">
          <TabRelatorios podeVisualizar={podeVisualizarRelatorios} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
